import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loginUser, clearError } from '../store/slices/authSlice';
import { fetchProfile } from '../store/slices/profileSlice';
import { apiService } from '../services/apiService';
import { branding } from '../constants/branding';
import './Login.css';

interface LoginForm {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  // Load saved preferences from localStorage
  const getSavedForm = (): LoginForm => {
    try {
      const saved = localStorage.getItem('retailLoginForm');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { username: parsed.username || '', password: parsed.password || '' };
      }
    } catch (e) {
      console.error('Error loading saved form:', e);
    }
    return { username: '', password: '' };
  };
  
  const getSavedLoginType = (): 'web' | 'api' | null => {
    try {
      const saved = localStorage.getItem('retailLoginType');
      if (!saved) return null;
      return saved === 'session' ? 'web' : 'api';
    } catch (e) {
      return null;
    }
  };
  
  const [form, setForm] = useState<LoginForm>(getSavedForm());
  const [otpCode, setOtpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authType, setAuthType] = useState<'web' | 'api'>('api');
  const [showLoginTypeSelector, setShowLoginTypeSelector] = useState(true);
  const [loginDisplaySettingsLoading, setLoginDisplaySettingsLoading] = useState(true);
  const [requiresOtp, setRequiresOtp] = useState(false); // Track if OTP is required
  
  const { isAuthenticated, isLoading, error } = useAppSelector(state => state.auth);
  
  // Save form data to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('retailLoginForm', JSON.stringify(form));
    } catch (e) {
      console.error('Error saving form:', e);
    }
  }, [form]);
  
  // Save login type preference to localStorage when it changes
  useEffect(() => {
    if (!showLoginTypeSelector) return;
    try {
      localStorage.setItem('retailLoginType', authType === 'web' ? 'session' : 'jwt');
    } catch (e) {
      console.error('Error saving login type:', e);
    }
  }, [authType, showLoginTypeSelector]);

  // Load login display settings (public endpoint)
  useEffect(() => {
    const loadLoginDisplaySettings = async () => {
      try {
        const response = await apiService.get<{
          defaultLoginType?: string;
          showRetailLoginTypeSelector?: boolean;
        }>('/api/public/auth/login-display-settings');

        if (response.success && response.data) {
          const defaultAuthType = response.data.defaultLoginType === 'SESSION' ? 'web' : 'api';
          const showSelector = response.data.showRetailLoginTypeSelector !== false;
          const savedType = getSavedLoginType();
          setShowLoginTypeSelector(showSelector);
          setAuthType(showSelector && savedType ? savedType : defaultAuthType);
          return;
        }
      } catch (e) {
        console.error('Error loading retail login display settings:', e);
      }

      const savedType = getSavedLoginType();
      setAuthType(savedType || 'api');
      setShowLoginTypeSelector(true);
      setLoginDisplaySettingsLoading(false);
      return;
    };

    const run = async () => {
      await loadLoginDisplaySettings();
      setLoginDisplaySettingsLoading(false);
    };

    void run();
  }, []);

  // Existing session: send lapsed subscribers to renewal-only shell
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    void (async () => {
      try {
        const subRes = await apiService.get<{ status?: string }>('/api/retail/subscription/status');
        if (cancelled) return;
        const st = subRes.success && subRes.data ? subRes.data.status : undefined;
        if (st === 'ACTIVE' || st === 'GRACE_PERIOD') {
          await dispatch(fetchProfile());
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/renew-subscription', { replace: true });
        }
      } catch {
        if (!cancelled) navigate('/dashboard', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, navigate, dispatch]);

  // Check error message and set requiresOtp if OTP is required
  useEffect(() => {
    if (error && (error.includes('OTP required') || error.includes('OTP code') || error.includes('Two-factor authentication'))) {
      setRequiresOtp(true);
    }
  }, [error]);

  // Don't render anything while checking authentication or if already authenticated
  if (isLoading || isAuthenticated || loginDisplaySettingsLoading) {
    return null;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear Redux error if it's not OTP-related
    if (error && !requiresOtp && !error.includes('OTP') && !error.includes('Two-factor')) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError()); // Clear any previous errors
    
    try {
      const result = await dispatch(loginUser({ 
        username: form.username, 
        password: form.password,
        otpCode: requiresOtp ? otpCode : undefined,
        headers: { 'X-Type': authType } // Add X-Type header based on selected auth type
      }));
      
      if (loginUser.fulfilled.match(result)) {
        setRequiresOtp(false); // Reset OTP requirement on success
        setOtpCode(''); // Clear OTP code
        try {
          const subRes = await apiService.get<{ status?: string }>('/api/retail/subscription/status');
          const st = subRes.success && subRes.data ? subRes.data.status : undefined;
          if (st === 'ACTIVE' || st === 'GRACE_PERIOD') {
            await dispatch(fetchProfile());
            navigate('/dashboard');
          } else {
            navigate('/renew-subscription');
          }
        } catch {
          await dispatch(fetchProfile());
          navigate('/dashboard');
        }
      } else if (loginUser.rejected.match(result)) {
        // Error is automatically set in Redux by the thunk
        // The useEffect will detect OTP errors and set requiresOtp
        const errorMessage = result.payload as string;
        if (errorMessage && (errorMessage.includes('OTP required') || errorMessage.includes('OTP code') || errorMessage.includes('Two-factor authentication'))) {
          setRequiresOtp(true);
        }
      }
    } catch (error) {
      // Error handling is done by the thunk
      console.error('Login error:', error);
    }
  };


  return (
    <div className="min-h-screen bg-zazoo-light-grey flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-zazoo-accent-blue rounded-full flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-3xl font-nunito font-bold text-gray-900">Zogzila Login v2.0</h2>
          <p className="mt-2 text-sm font-nunito text-gray-600">Sign in to your account</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                Username
              </label>
              <input
                id="username"
                type="text"
                name="username"
                value={form.username}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue focus:border-transparent"
                placeholder="Enter your username"
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue focus:border-transparent"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* OTP Code Field - Shown when 2FA is required */}
            {requiresOtp ? (
              <div>
                <label htmlFor="otpCode" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                  Two-Factor Authentication Code
                </label>
                <input
                  id="otpCode"
                  type="text"
                  value={otpCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
                    setOtpCode(value);
                  }}
                  required={requiresOtp}
                  disabled={isLoading}
                  maxLength={6}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue focus:border-transparent"
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
                <p className="mt-2 text-xs font-nunito text-gray-500">
                  Enter the 6-digit code from your Google Authenticator app
                </p>
              </div>
            ) : null}

            {/* Authentication Type Selector */}
            {showLoginTypeSelector && (
              <div className="form-group">
                <label className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                  Login Type
                </label>
                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="authType"
                      value="web"
                      checked={authType === 'web'}
                      onChange={(e) => setAuthType(e.target.value as 'web' | 'api')}
                      disabled={isLoading}
                      className="h-4 w-4 text-zazoo-accent-blue focus:ring-zazoo-accent-blue border-gray-300"
                    />
                    <span className="ml-2 text-sm font-nunito text-gray-700">Web (Session)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="authType"
                      value="api"
                      checked={authType === 'api'}
                      onChange={(e) => setAuthType(e.target.value as 'web' | 'api')}
                      disabled={isLoading}
                      className="h-4 w-4 text-zazoo-accent-blue focus:ring-zazoo-accent-blue border-gray-300"
                    />
                    <span className="ml-2 text-sm font-nunito text-gray-700">API (JWT)</span>
                  </label>
                </div>
              </div>
            )}
            
            {error && (
              <div className="flex items-start gap-3 p-4 bg-zazoo-red bg-opacity-10 border border-zazoo-red rounded-lg">
                <svg className="w-5 h-5 text-zazoo-red flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="font-nunito text-zazoo-red text-sm">{error}</p>
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoading || !form.username || !form.password || (requiresOtp && otpCode.length !== 6)}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-mukta font-medium text-white bg-zazoo-accent-blue hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zazoo-accent-blue disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {requiresOtp ? 'Verifying...' : 'Signing in...'}
                </>
              ) : (
                requiresOtp ? 'Verify OTP Code' : 'Sign in to Banking'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button 
              className="font-nunito text-zazoo-accent-pink hover:text-zazoo-accent-blue"
              onClick={() => navigate('/admin/login')}
            >
              Admin Login
            </button>
          </div>
        </div>

        <div className="text-center">
          <p className="text-xs font-nunito text-gray-500">
            {/* Demo credentials: user@example.com / password123 */}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login; 